import React, { useEffect, useState } from "react";
import axios from "axios";
import { Upload, X, Menu } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const HomeAboutForm = () => {
  const [formData, setFormData] = useState({
    subtitle: "",
    title: "",
    description: "",
    button: "",
    image: "",
  });
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false); // ✅ For mobile hamburger

  // =============================
  // Fetch existing About data
  // =============================
  const fetchAbout = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/home/about`);
      if (data) {
        setFormData({
          subtitle: data.subtitle || "",
          title: data.title || "",
          description: data.description || "",
          button: data.button || "",
          image: data.image || "",
        });
      }
    } catch (error) {
      console.error("Error fetching Home About:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAbout();
  }, []);

  // =============================
  // Handle Change
  // =============================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // =============================
  // Handle Image Upload
  // =============================
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append("media", file);

    axios
      .post(`${API_BASE_URL}/admin/upload-media`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        const uploadedUrl = res.data.data.files?.[0]?.url;
        if (uploadedUrl) {
          setFormData((prev) => ({ ...prev, image: uploadedUrl }));
        }
      })
      .catch((err) => {
        console.error("upload-media error", err);
        alert("Failed to upload image. Please try again.");
      });
  };

  // =============================
  // Remove Image
  // =============================
  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: "" }));
  };

  // =============================
  // Submit Form
  // =============================
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API_BASE_URL}/home/about`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      alert("Home About updated successfully!");
      fetchAbout();
    } catch (error) {
      console.error("Error saving Home About:", error);
      alert("Failed to save Home About section.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative w-full min-h-screen bg-gray-50 flex flex-col md:flex-row">
      {/* ✅ Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-blue-600 text-white px-4 py-3 shadow">
        <h1 className="font-semibold text-lg">Home – About</h1>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="p-2 rounded hover:bg-blue-700"
        >
          <Menu size={22} />
        </button>
      </div>

      {/* ✅ Sidebar Menu for Tablet/Mobile */}
      {menuOpen && (
        <div className="absolute top-0 left-0 w-64 h-full bg-white shadow-lg z-50 p-6 transition-all duration-300">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold">Menu</h2>
            <button
              onClick={() => setMenuOpen(false)}
              className="text-gray-600 hover:text-gray-800"
            >
              <X size={22} />
            </button>
          </div>
          <nav className="space-y-3">
            <button
              onClick={() => setMenuOpen(false)}
              className="w-full text-left text-gray-700 hover:text-blue-600 font-medium"
            >
              Dashboard
            </button>
            <button
              onClick={() => setMenuOpen(false)}
              className="w-full text-left text-gray-700 hover:text-blue-600 font-medium"
            >
              Projects
            </button>
            <button
              onClick={() => setMenuOpen(false)}
              className="w-full text-left text-gray-700 hover:text-blue-600 font-medium"
            >
              Settings
            </button>
          </nav>
        </div>
      )}

      {/* ✅ Main Form */}
      <div className="flex-1 p-4 sm:p-6 md:p-8 lg:p-10 bg-white rounded-lg shadow-md mt-4 md:mt-0 mx-3 sm:mx-6 md:mx-8 overflow-y-auto">
        <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-center md:text-left text-blue-600">
          About Us
        </h2>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 text-sm sm:text-base"
        >
          <div>
            <label className="block font-medium mb-1">Subtitle</label>
            <input
              type="text"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              className="w-full border p-2 rounded"
            />
          </div>

          <div>
            <label className="block font-medium mb-1">Title</label>
            <input
              type="text"
              name="title"
              required
              value={formData.title}
              onChange={handleChange}
              className="w-full border p-2 rounded"
            />
          </div>

          <div>
            <label className="block font-medium mb-1">Description </label>
            <textarea
              name="description"
              rows="4"
              required
              value={formData.description}
              onChange={handleChange}
              className="w-full border p-2 rounded resize-none"
            ></textarea>
          </div>

          <div>
            <label className="block font-medium mb-1">Button Label</label>
            <input
              type="text"
              name="button"
              value={formData.button}
              onChange={handleChange}
              className="w-full border p-2 rounded"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block font-medium mb-2">Image</label>
            {formData.image ? (
              <div className="relative inline-block w-full sm:w-auto">
                <img
                  src={formData.image}
                  alt="About preview"
                  className="w-full sm:max-w-md h-52 sm:h-64 object-cover rounded-lg border"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 sm:p-2 rounded-lg"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center">
                <input
                  type="file"
                  id="about-image-upload"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <label
                  htmlFor="about-image-upload"
                  className="cursor-pointer flex flex-col items-center gap-3"
                >
                  <Upload size={36} className="text-gray-400" />
                  <span className="text-gray-600 text-sm sm:text-base">
                    Click to upload image
                  </span>
                  <span className="text-xs text-gray-400">
                  Recommended image size: 1200 × 1600 px (3:4), JPG/PNG, upto 350 KB
                  </span>
                </label>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white py-2 px-6 rounded hover:bg-blue-700 disabled:bg-gray-400 w-full sm:w-auto"
          >
            {loading ? "Saving..." : "Save About Section"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default HomeAboutForm;
